import { Button, Card, Icon, MonoLabel } from '@/design-system';
import type { Onboarding } from '@/entities/order';
import type { TenantTrial } from '@/entities/tenant';
import { formatDayLong } from '@/shared/lib';

import { dashboardCopy } from '../copy';
import { trialUsedUp, type NextStep } from '../getting-started';

export interface GettingStartedCardProps {
    /** De ene stap die nu telt; zie `nextStepFor`. */
    next: NextStep;
    trial: TenantTrial | null;
    /** De eerste stappen, of `null` als ze klaar of verborgen zijn. */
    onboarding: Onboarding | null;
    /**
     * De lege staat: dan is dit het enige op het scherm en is de knop de groene
     * hoofdactie (de knop "Nieuwe keuring" in de kop is er dan niet). Daarnaast
     * is hij donker, zodat er nooit twee groene knoppen staan.
     */
    alone: boolean;
    hiding: boolean;
    onAct: (step: NextStep) => void;
    onHide: () => void;
}

/**
 * "Aan de slag": de proef en de eerste stappen in één blok, met één knop.
 *
 * Bovenaan staat in gewone woorden wat nu de volgende stap is, met één knop die
 * hem zet. Daaronder, rustig: hoeveel gratis keuringen er nog zijn, en het lijstje
 * eerste stappen met vinkjes. Zonder lijstje (verborgen of klaar) en zonder
 * keuringen staat er hoe een keuring gaat, zodat de lege staat nooit leeg is.
 *
 * Geen alarmkleur, ook niet als de proef op is: een bedrijf dat net begint hoort
 * te zien wat het kan, niet wat straks niet meer kan.
 */
export function GettingStartedCard({
    next,
    trial,
    onboarding,
    alone,
    hiding,
    onAct,
    onHide,
}: GettingStartedCardProps) {
    const usedUp = trialUsedUp(trial);
    const text = dashboardCopy.nextSteps[next];
    const title = next === 'choose_plan' && usedUp ? dashboardCopy.trialUsedTitle : text.title;
    const body = next === 'choose_plan' && usedUp ? dashboardCopy.trialUsedBody : text.body;
    const done = onboarding?.steps.filter((step) => step.done).length ?? 0;

    return (
        <Card padding="lg" as="section" aria-label={dashboardCopy.gettingStartedTitle}>
            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                <MonoLabel size={10} as="div" className="tracking-[0.1em]">
                    {dashboardCopy.gettingStartedTitle}
                </MonoLabel>
                {onboarding ? (
                    <div className="flex items-center gap-3">
                        <MonoLabel size={11}>
                            {dashboardCopy.gettingStartedProgress(done, onboarding.steps.length)}
                        </MonoLabel>
                        <Button variant="ghost" disabled={hiding} onClick={onHide}>
                            {dashboardCopy.gettingStartedHide}
                        </Button>
                    </div>
                ) : null}
            </div>

            <h2 className="m-0 mt-2 font-[family-name:var(--font-display)] text-[19px] leading-[1.25] font-semibold tracking-[-0.01em] text-[var(--text-primary)]">
                {title}
            </h2>
            <p className="m-0 mt-2 max-w-[62ch] text-[14px] leading-[1.5] text-[var(--text-secondary)]">
                {body}
            </p>

            <Button
                variant={alone ? 'primary' : 'dark'}
                size="sm"
                icon={next === 'first_inspection' ? 'add' : undefined}
                className="mt-[14px]"
                data-tour={alone && next === 'first_inspection' ? 'new-inspection' : undefined}
                onClick={() => onAct(next)}
            >
                {text.action}
            </Button>

            {trial && !usedUp ? (
                <p className="m-0 mt-[14px] flex flex-wrap items-baseline gap-x-3 gap-y-1 text-[13px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                    <span>
                        {trial.inspectionsRemaining === null
                            ? dashboardCopy.trialRunning
                            : dashboardCopy.trialRemaining(trial.inspectionsRemaining)}
                        {trial.endsAt
                            ? ` ${dashboardCopy.trialEndsOn(formatDayLong(trial.endsAt))}`
                            : ''}
                    </span>
                    {next === 'choose_plan' ? null : (
                        <Button variant="ghost" onClick={() => onAct('choose_plan')}>
                            {dashboardCopy.choosePlan}
                        </Button>
                    )}
                </p>
            ) : null}

            {onboarding ? (
                <ol className="m-0 mt-4 list-none border-t border-[var(--border-subtle-2)] p-0">
                    {onboarding.steps.map((step) => {
                        const label = dashboardCopy.onboardingSteps[step.key];

                        return (
                            <li
                                key={step.key}
                                className="grid min-h-[44px] grid-cols-[auto_1fr_auto] items-center gap-3 border-b border-[var(--border-subtle-2)] py-[6px] last:border-b-0"
                            >
                                {step.done ? (
                                    <Icon
                                        name="check"
                                        size="sm"
                                        tone="brand"
                                        label={dashboardCopy.stepDone}
                                    />
                                ) : (
                                    <span
                                        aria-hidden="true"
                                        className="block h-4 w-4 rounded-full border border-[var(--border-control)]"
                                    />
                                )}
                                <span
                                    className={
                                        step.done
                                            ? 'text-[14px] text-[var(--text-tertiary-safe)]'
                                            : 'text-[14px] text-[var(--text-primary)]'
                                    }
                                >
                                    {label.label}
                                </span>
                                {step.done || step.key === next ? null : (
                                    <Button variant="ghost" onClick={() => onAct(step.key)}>
                                        {label.action}
                                    </Button>
                                )}
                            </li>
                        );
                    })}
                </ol>
            ) : alone ? (
                <div className="mt-4 border-t border-[var(--border-subtle-2)] pt-[14px]">
                    <MonoLabel size={10} as="div" className="mb-2 tracking-[0.1em]">
                        {dashboardCopy.howItWorksLabel}
                    </MonoLabel>
                    <ol className="m-0 grid list-none gap-2 p-0">
                        {dashboardCopy.howItWorks.map((line, index) => (
                            <li
                                key={line}
                                className="grid grid-cols-[22px_1fr] items-baseline gap-2 text-[14px] leading-[1.5] text-[var(--text-secondary)]"
                            >
                                <MonoLabel size={11} variant="value" tone="default">
                                    {index + 1}
                                </MonoLabel>
                                {line}
                            </li>
                        ))}
                    </ol>
                </div>
            ) : null}
        </Card>
    );
}
